import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/Header';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { financeService } from '@/services/financeService';
import { toast } from 'sonner';

// Import components
import { PlanSummaryCard } from '@/components/finance/PlanSummaryCard';
import { UsageStatsCard } from '@/components/finance/UsageStatsCard';
import { PaymentMethodsCard } from '@/components/finance/PaymentMethodsCard';
import { InvoiceHistoryCard } from '@/components/finance/InvoiceHistoryCard';
import { SubscriptionStatusCard } from '@/components/finance/SubscriptionStatusCard';

export default function Finance() {
  useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const { user } = useCustomAuth();

  // Busca dados reais do dashboard financeiro
  const { data: dashboardData, isLoading, error } = useQuery<any>({
    queryKey: ['financeDashboard', user?.id_Stripe_Client],
    queryFn: async () => {
      if (!user?.id_Stripe_Client) {
        throw new Error(translateInterfaceText("ID do cliente Stripe não encontrado"));
      }
      return await financeService.getDashboard(user.id_Stripe_Client);
    },
    enabled: !!user?.id_Stripe_Client,
    retry: 1,
  });

  // Dados temporários para gráfico de uso (até integrar com API)
  const usageData = [
    { day: '01/11', date: '01/11', plays: dashboardData?.usage.used ? Math.floor(dashboardData.usage.used * 0.6) : 0 },
    { day: '02/11', date: '02/11', plays: dashboardData?.usage.used ? Math.floor(dashboardData.usage.used * 0.8) : 0 },
    { day: '03/11', date: '03/11', plays: dashboardData?.usage.used || 0 },
  ];

  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col lg:overflow-hidden mobile-scroll-fix">
        <Header title={translateInterfaceText("Financeiro")} />

        {/* Divisor horizontal padrão HostVSL */}
        <div className="hidden lg:block h-px bg-border w-full" />

        <main className="flex-1 lg:overflow-y-auto">
          <div ref={containerRef} className="container mx-auto px-4 md:px-6 py-6 md:py-8 pb-[calc(120px+env(safe-area-inset-bottom))] lg:pb-8 space-y-7 max-w-6xl">
            {/* Plan Summary + Payment Methods - lado a lado */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PlanSummaryCard
                planData={dashboardData?.plan}
                isLoading={isLoading}
              />
              <PaymentMethodsCard
                paymentMethods={dashboardData?.payment_methods}
                isLoading={isLoading}
                containerRef={containerRef}
              />
            </div>

            {/* Usage Stats + Invoice History - lado a lado */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <UsageStatsCard
                usage={dashboardData?.usage}
                isLoading={isLoading}
                usageData={usageData}
              />
              <InvoiceHistoryCard
                invoices={dashboardData?.invoices || []}
                isLoading={isLoading}
              />
            </div>

            {/* Subscription Status */}
            <SubscriptionStatusCard
              showCancelDialog={showCancelDialog}
              setShowCancelDialog={setShowCancelDialog}
              containerRef={containerRef}
              subscription={dashboardData?.subscription}
              isLoading={isLoading}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
}